import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { getClassroom } from '@app/classrooms/_store/classroom/classroom.selectors';
import {
  clearClassroomMeetings,
  loadClassroomMeetings,
  loadNextPageClassroomMeetings,
} from '@app/classrooms/_store/meetings';
import {
  getClassroomMeetingIsNotEmpty,
  getClassroomMeetings,
  getClassroomMeetingsLoaded,
  getClassroomMeetingsLoading,
  getClassroomMeetingsPagination,
} from '@app/classrooms/_store/meetings/meetings.selectors';
import { moveFadeIn, moveFadeOut } from '@core/animations';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { compareMeetingsByDateASC } from '@store/reducers/profile.reducer';
import { Classroom, EDashboardMeetingGroup, IPagination, Meeting, MeetingStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { map, mergeMap, Observable, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'app-classroom-meetings-page',
  templateUrl: './classroom-meetings-page.component.html',
  styleUrls: ['./classroom-meetings-page.component.scss'],
  animations: [
    trigger('meeting', [
      transition(':enter', useAnimation(moveFadeIn, { params: { time: '200ms', slide: '5px' } })),
      transition(':leave', useAnimation(moveFadeOut)),
    ]),
  ],
  /** TODO: C onPush вообще не работает компонент pagination. Надо исправить компонент */
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassroomMeetingsPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  public loading$: Observable<boolean>;
  public loaded$: Observable<boolean>;
  public notEmpty$: Observable<boolean>;
  public pagination$: Observable<IPagination>;
  public meetingStatusEnum = MeetingStatusEnum;
  public stub: string;
  public isOwner: boolean;
  public newClassOpenForm: boolean;
  public classroom: Classroom;

  private _meetings: Meeting[];
  public categoryTabs = [];
  public selectedTab: any = { id: -1, code: 'all', title: 'All' };

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private store: Store,
    protected cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.loadMeetings();
    this.loaded$ = this.store.select(getClassroomMeetingsLoaded).pipe(tap(() => this.detectChanges()));
    this.loading$ = this.store.select(getClassroomMeetingsLoading).pipe(tap(() => this.detectChanges()));
    this.pagination$ = this.store.select(getClassroomMeetingsPagination).pipe(tap(() => this.detectChanges()));
    this.notEmpty$ = this.store.select(getClassroomMeetingIsNotEmpty).pipe(tap(() => this.detectChanges()));
  }

  loadMeetings() {
    this.store
      .select(getClassroom)
      .pipe(
        map((classroom) => {
          this.classroom = classroom;
          this.stub = classroom.visit_info?.is_author
            ? 'my-classes.stub.stub1'
            : 'classrooms.classroom-page.meetings.stubs.participated.description';
          this.isOwner = classroom.visit_info?.is_author;
          this.store.dispatch(clearClassroomMeetings());
          this.store.dispatch(loadClassroomMeetings());
          return classroom;
        }),
        mergeMap(() =>
          this.store.select(getClassroomMeetings).pipe(
            tap((meetings) => {
              this._meetings = meetings;
              this.categoryTabs = this.tabs;
              this.detectChanges();
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        }
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(clearClassroomMeetings());
  }

  public isTabVisible(code: EDashboardMeetingGroup) {
    return this._meetings?.filter((e) => e.has.meetingGroup === Number(code)).length;
  }

  public get tabs() {
    const tabs = [
      { id: -1, code: 'all', title: 'my-classes.no-events.filter.All', visible: true },
      {
        id: 1,
        code: EDashboardMeetingGroup.upcoming,
        title: 'my-classes.no-events.filter.Upcoming',
        visible: this.isTabVisible(EDashboardMeetingGroup.upcoming),
      },
      {
        id: 2,
        code: EDashboardMeetingGroup.unconfirmed,
        title: 'my-classes.no-events.filter.Unconfirmed',
        visible: this.isTabVisible(EDashboardMeetingGroup.unconfirmed),
      },
      {
        id: 3,
        code: EDashboardMeetingGroup.past,
        title: 'my-classes.no-events.filter.Past',
        visible: this.isTabVisible(EDashboardMeetingGroup.past),
      },
      {
        id: 4,
        code: EDashboardMeetingGroup.canceled,
        title: 'my-classes.no-events.filter.Canceled',
        visible: this.isTabVisible(EDashboardMeetingGroup.canceled),
      },
    ];
    return tabs.filter((el) => el.visible);
  }

  public onToggleMeetingForm() {
    this.newClassOpenForm = !this.newClassOpenForm;
  }

  public onMeetingSaved() {
    this.onToggleMeetingForm();
    this.store.dispatch(clearClassroomMeetings());
    this.store.dispatch(loadClassroomMeetings());
  }

  public trackMeetingByFn(index, item: Meeting): string {
    return item.id;
  }

  public nextPage() {
    this.store.dispatch(loadNextPageClassroomMeetings());
    this.detectChanges();
  }

  public onChanged() {
    this.store.dispatch(clearClassroomMeetings());
    this.store.dispatch(loadClassroomMeetings());
  }

  public onSelectItem(item, force = false): void {
    if (this.selectedTab.id === item.id && !force) {
      return;
    }
    this.selectedTab = item;
  }

  public get filteredMeetings(): Meeting[] {
    if (this.selectedTab.code === 'all') {
      return this._meetings;
    }
    if (
      this.selectedTab.code === EDashboardMeetingGroup.upcoming ||
      this.selectedTab.code === EDashboardMeetingGroup.unconfirmed
    ) {
      return this._meetings.filter((e) => e.has.meetingGroup === this.selectedTab.code).sort(compareMeetingsByDateASC);
    }
    return this._meetings.filter((e) => e.has.meetingGroup === this.selectedTab.code);
  }
}
